
import { watch } from 'vue';
import { child, set, push } from 'firebase/database';
import { MobileV1Table } from '~/class/MobileV1Table';
import AdapterMobileHistoryV1 from '~/class/AdapterMobileHistoryV1';




// v1 sync to for mobile use.
export default defineNuxtPlugin(() => {


    const jobStore = usePosJobsStore()
    const tableStateStore = usePosTableStatesStore()
    const shopStore = usePosShopStore()

    // Mobile-v1 cloud sync is a Main-Cashier-only responsibility. Terminal /
    // 2nd-cashier / readonly devices must never publish to the mobile nodes —
    // they'd race the real cashier and overwrite its state. runMode is loaded
    // later in bootstrap, so we gate at write-time (not plugin-init time) and
    // re-read it on every publish, which also handles a mid-session mode switch.
    const isMainCashier = () => shopStore.runMode === "cashier"

    // Cheap change-detector per table, keyed by the SAME alias key jobStore.tables
    // uses. See buildTableFingerprint — it is deliberately NOT the published
    // payload: on a busy shop this reconcile runs once per computeJobchain, i.e.
    // once per job, and a 60-occupied-table shop building the real payload for
    // every table cost ~1.5ms and 541KB of throwaway string per job. The
    // fingerprint costs ~0.07ms / 17KB, and only the tables it flags actually get
    // transformed and published — normally one.
    let _fingerprints: Record<string, string> = {}

    // The first reconcile after boot SEEDS _fingerprints without writing
    // anything. The main cashier is the only writer of these nodes (isMainCashier
    // gate below), so whatever is in the cloud is what THIS role published last
    // session — a reload must continue from it, not re-push it. Without this a
    // 90-table shop would fire 180 RTDB writes (list + tables_v2/lines per table)
    // on every page refresh for data that is already identical.
    //
    // Note this means we do not repair a cloud node that drifted while the POS was
    // closed. That is deliberate: the cleanup path is Close Shop, which clears the
    // mobile nodes outright — a reload is not the place to re-sync 90 tables.
    let _firstRun = true

    // customercounts is written from TWO watchers (tablesState + jobStore.tables)
    // with the same derived array — dedupe on the serialized payload so only a
    // real change costs an RTDB write. Same pattern as cloud.mobile-v2.plugin's
    // lastLayoutJson / lastSummaryJson.
    let _lastCustomerCountsJson = ""


    jobStore.$onAction(({
        name, // name of the action
        store, // store instance, same as `someStore`
        args, // array of parameters passed to the action
        after, // hook after the action returns or resolves
        onError, // hook if the action throws or rejects
    }) => {
        if (name == "add") {
            const job = args[0]
            const isSync = args[1]
            after((returnPayload) => {
                // Mirror the card into the legacy per-table history silo (ADD + VOID).
                // This is the ONLY thing that belongs on the job hook: history is an
                // append-only log of rounds, it cannot be derived from table state.
                // The table snapshot (tables/{uuid}/list) is published by the
                // jobStore.tables watcher below instead — see the note there.
                writeHistoryRecord(job);
            })
        }
    })

    watch(() => tableStateStore.tablesState, updateCustomerCounts, { deep: true })


    // Single source of truth for every per-table mobile publish.
    //
    // This used to run off the add-action after-hook on a fixed 600ms timer, which
    // was a race we lost regularly: add() deliberately does NOT recompute
    // jobStore.tables (see pos.jobs.ts) — only the RTDB echo does, behind a 200ms
    // debounce — so on a slow shop network the timer fired while tables still held
    // the PRE-job state and we published the OLD price. Nothing re-published
    // afterwards, so the stale value sat on the phone until the next job on that
    // table. That is the "Change Price doesn't reach the phone" bug.
    //
    // Driving it off jobStore.tables instead means we publish exactly when the
    // recomputed state exists, so it is timing-independent and covers cases the
    // job hook could never cover:
    //   - MOVE_TO: the DESTINATION table (job.newAlias) was never published.
    //   - guests / service charge / whole-bill discount: those write to
    //     tables_state and create NO job at all, yet they change grandTotal.
    // The watcher is intentionally shallow — computeJobchain assigns a brand new
    // object every run, so ref replacement is the signal, and a deep watch would
    // just cost more.
    watch(() => jobStore.tables, () => {
        updateCustomerCounts()
        reconcileTablesToMobileV1Cloud()
    })

    function reconcileTablesToMobileV1Cloud() {
        if (!isMainCashier()) return

        const tables = (jobStore.tables || {}) as Record<string, any>
        const nextFingerprints: Record<string, string> = {}

        for (const tableName in tables) {
            const table = tables[tableName]
            if (!table) continue

            // TOGO / JOB / DELIVERY aliases are hashes, not zone labels, so they are
            // not in the uuid map and have no home on the legacy phone app. Bail
            // before doing any work — the old code wrote them to `tables/null/...`.
            const uuid = usePosMobileV1Store().convertTableToUuid(tableName)
            if (!uuid) continue

            const fingerprint = buildTableFingerprint(table)
            nextFingerprints[tableName] = fingerprint

            if (_firstRun) continue
            if (_fingerprints[tableName] === fingerprint) continue

            publishTableToMobileV1Cloud(uuid, table)
        }

        // Tables that left the jobchain (paid in full / cleared / moved away) —
        // clear their mobile node so a stale SubTotal/GrandTotal doesn't linger on
        // the phone's Info tab.
        if (!_firstRun) {
            for (const tableName in _fingerprints) {
                if (!(tableName in nextFingerprints)) {
                    removeTableFromMobileV1Cloud(tableName)
                }
            }
        }

        _fingerprints = nextFingerprints
        _firstRun = false
    }

    // Cheap, allocation-light signature of everything the phone can actually see.
    //
    // It reads the same source fields MobileV1Table.transform/buildLines read, so it
    // cannot miss a visible change the way a summary-only fingerprint would (the
    // tables_v3 plugin's old `items.length-grandTotal` silently dropped every line
    // comment). It deliberately does NOT key off `lastUpdate`: that would be O(1)
    // but JobchainV1_1 only stamps it on the SOURCE table of a MOVE_TO, so the
    // destination would never republish.
    //
    // `billing` is stringified whole (it is a small fixed-shape object) because the
    // summary rows — vat / service / discount / cardCharge — all come from it.
    // Toppings are covered by toppingsAmount + count: no job type mutates a
    // topping's name, they only ever arrive with the ADD.
    function buildTableFingerprint(table: any): string {
        let out = `${table.name}|${table.guests}|${table.startDateTime}|${JSON.stringify(table.billing)}`
        const items = table.items || []
        for (const i of items) {
            out += `|${i.uuid},${i.subItemId},${i.name},${i.comments},${i.price},${i.quantity}`
                + `,${i.extendedPrice},${i.discountedPrice},${i.dc1},${i.toppingsAmount},${(i.toppings || []).length}`
        }
        return out
    }

    // LATENT-LOOP NOTE: this pushes into tables/{n}/history — the SAME node the
    // cashier's own onChildAdded ingest (pos.mobile-v1.ts watchFromMobileV1)
    // subscribes to. Three independent layers keep our own write from being
    // re-ingested as a "mobile order" (which would add() → push here → ∞):
    //   1. push() keys start with "-", which sorts BEFORE the numeric epoch
    //      string in the ingest query's startAt() under orderByKey.
    //   2. The record carries read: 2 (AdapterMobileHistoryV1).
    //   3. handleMobileV1 rejects push-keyed children and read == 2 outright.
    // Do not change the history key scheme or drop read: 2 without re-checking
    // all three.
    function writeHistoryRecord(job: any) {
        if (!isMainCashier()) return
        const { tableNumber, record } = new AdapterMobileHistoryV1().fromJobToHistory(job)
        if (!tableNumber || !record) return

        const customerRootRef = useRealtimeDatabase().customerRootRef();
        const history_ref = child(customerRootRef, `tables/${tableNumber}/history`);
        // Child VALUE must be a JSON string — HistoryFragmentV2 does
        // value.toString() → new JSONObject(...). push() keeps chronological order.
        push(history_ref, JSON.stringify(record));
    }

    function updateCustomerCounts() {
        if (!isMainCashier()) return
        const json = JSON.stringify(usePosMobileV1Store().generateCustomerArray());
        if (json === _lastCustomerCountsJson) return
        _lastCustomerCountsJson = json
        const customerRootRef = useRealtimeDatabase().customerRootRef();
        set(child(customerRootRef, `tables/customercounts`), json);
    }


    // Only ever called for a table the fingerprint flagged as changed, so the
    // transform/stringify cost is paid once per real change, not once per table
    // per job.
    function publishTableToMobileV1Cloud(uuid: any, table: any) {
        const customerRootRef = useRealtimeDatabase().customerRootRef();
        const builder = new MobileV1Table();

        // The phone parses this node with value.toString() → new JSONObject(...),
        // so it MUST be a JSON string, not a real map.
        let list_ref = child(customerRootRef, `tables/${uuid}/list`);
        set(list_ref, JSON.stringify(builder.transform(table)));

        // Publish raw line objects for the legacy Void screen (VoidItemsActivity),
        // which reads customer/{storeId}/tables_v2/{uuid}/lines and iterates the
        // children via OrderLine.fromJSONObject — so this MUST be a real array/map,
        // NOT a JSON string (unlike tables/{uuid}/list above). uniqueId === the
        // cart-line uuid so the void round-trip (mapVoidUniqueId) matches.
        let voidLines_ref = child(customerRootRef, `tables_v2/${uuid}/lines`);
        set(voidLines_ref, builder.buildLines(table.items || []));
    }

    function removeTableFromMobileV1Cloud(tablename: string) {
        if (!isMainCashier()) return

        let uuid = usePosMobileV1Store().convertTableToUuid(tablename)
        if (!uuid) {
            return
        }
        const customerRootRef = useRealtimeDatabase().customerRootRef();

        let list_ref = child(customerRootRef, `tables/${uuid}`);
        set(list_ref, null);

        // Clear the legacy Void screen's line source too.
        let voidLines_ref = child(customerRootRef, `tables_v2/${uuid}`);
        set(voidLines_ref, null);
    }
})


